/**
 * Home Slideshow Component
 * Handles the main homepage slideshow with Swiper, autoplay, navigation, and pagination
 */
export class HomeSlideshow extends HTMLElement {
  swiper = null

  connectedCallback() {
    // Show loader initially
    this.showLoader()

    // Wait for Swiper to be available
    if (
      !window.Swiper ||
      !window.SwiperNavigation ||
      !window.Pagination ||
      !window.Autoplay
    ) {
      // Retry after a short delay
      setTimeout(() => this.connectedCallback(), 100)
      return
    }

    // Initialize Swiper
    this.initSwiper()
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true)
      this.swiper = null
    }
  }

  initSwiper() {
    const container = this.querySelector('.home-slideshow-swiper')
    if (!container) {
      // Hide loader if container not found
      this.hideLoader()
      return
    }

    const Swiper = window.Swiper
    const SwiperNavigation = window.SwiperNavigation
    const Pagination = window.Pagination
    const Autoplay = window.Autoplay

    // Get configuration from data attributes
    const loop = this.dataset.loop === 'true'
    const autoplayDelay = parseInt(this.dataset.autoplayDelay || '5000', 10)
    const speed = parseInt(this.dataset.speed || '300', 10)

    // Get navigation elements
    const nextButton = container.querySelector('[data-swiper-next]')
    const prevButton = container.querySelector('[data-swiper-prev]')
    const paginationEl = container.querySelector('[data-swiper-pagination]')

    // Capture reference to web component for use in callbacks
    const component = this

    // Wait a frame to ensure DOM is ready
    requestAnimationFrame(() => {
      try {
        const config = {
          modules: [SwiperNavigation, Pagination, Autoplay],
          speed: speed,
          direction: 'horizontal',
          effect: 'slide',
          slidesPerView: 1,
          spaceBetween: 0,
          loop: loop,
          cssMode: false,
          allowTouchMove: true,
          watchOverflow: false,
          resistance: true,
          resistanceRatio: 0.85,
          followFinger: true,
          threshold: 5,
          autoplay: {
            delay: autoplayDelay,
            disableOnInteraction: false, // Match original Alpine.js config
          },
          navigation:
            nextButton && prevButton
              ? {
                  nextEl: nextButton,
                  prevEl: prevButton,
                }
              : false,
          pagination: paginationEl
            ? {
                el: paginationEl,
                clickable: true,
              }
            : false,
          on: {
            init: function () {
              // Use requestAnimationFrame to ensure DOM is ready
              requestAnimationFrame(() => {
                // Remove x-cloak from all slides after Swiper is initialized
                const slides = component.querySelectorAll(
                  '.swiper-slide[x-cloak]'
                )
                slides.forEach((slide) => {
                  slide.removeAttribute('x-cloak')
                })

                // Also remove x-cloak from navigation buttons and pagination
                const navButtons = component.querySelectorAll(
                  '[data-swiper-prev][x-cloak], [data-swiper-next][x-cloak]'
                )
                navButtons.forEach((btn) => {
                  btn.removeAttribute('x-cloak')
                })

                const pagination = component.querySelectorAll(
                  '[data-swiper-pagination][x-cloak]'
                )
                pagination.forEach((el) => {
                  el.removeAttribute('x-cloak')
                })

                // Hide loader
                component.hideLoader()

                // Initialize countdowns
                component.initCountdowns()
              })
            },
            slideChange: function () {
              // Ensure navigation buttons remain enabled after slide changes
              if (loop) {
                const nextBtn = this.navigation?.nextEl
                const prevBtn = this.navigation?.prevEl
                if (nextBtn) nextBtn.classList.remove('swiper-button-disabled')
                if (prevBtn) prevBtn.classList.remove('swiper-button-disabled')
              }
            },
            reachEnd: function () {
              // When loop is enabled, buttons should always work
              if (loop) {
                const nextBtn = this.navigation?.nextEl
                const prevBtn = this.navigation?.prevEl
                if (nextBtn) nextBtn.classList.remove('swiper-button-disabled')
                if (prevBtn) prevBtn.classList.remove('swiper-button-disabled')
              }
            },
            reachBeginning: function () {
              // When loop is enabled, buttons should always work
              if (loop) {
                const nextBtn = this.navigation?.nextEl
                const prevBtn = this.navigation?.prevEl
                if (nextBtn) nextBtn.classList.remove('swiper-button-disabled')
                if (prevBtn) prevBtn.classList.remove('swiper-button-disabled')
              }
            },
          },
        }

        this.swiper = new Swiper(container, config)
      } catch (error) {
        console.error('Error initializing Swiper:', error)
        // Hide loader even if there's an error
        this.hideLoader()
      }
    })
  }

  showLoader() {
    const loader = this.querySelector('[data-swiper-loader]')
    if (loader) {
      loader.style.opacity = '1'
      loader.style.pointerEvents = 'auto'
      loader.style.display = 'flex'
    }
  }

  hideLoader() {
    const loader = this.querySelector('[data-swiper-loader]')
    if (loader) {
      loader.style.opacity = '0'
      loader.style.pointerEvents = 'none'
      // Remove loader from DOM after transition
      setTimeout(() => {
        if (loader.parentNode) {
          loader.remove()
        }
      }, 300)
    }
  }

  initCountdowns() {
    console.log('🕐 [HomeSlideshow] Initializing countdowns...')

    // Search for countdowns both inside the component and in the parent section
    // This handles countdowns in the huincha (outside component) and in slides (inside component)
    const section = this.closest('section') || document
    // Only initialize countdowns that haven't been initialized yet
    const countdowns = section.querySelectorAll('[data-countdown]:not([data-countdown-initialized])')

    console.log(`🕐 [HomeSlideshow] Found ${countdowns.length} uninitialized countdown(s)`)

    if (countdowns.length === 0) {
      console.log('🕐 [HomeSlideshow] No uninitialized countdowns found')
      return
    }

    countdowns.forEach((countdown, index) => {
      // Mark as initialized to prevent double initialization
      countdown.setAttribute('data-countdown-initialized', 'true')

      const endDateString = countdown.dataset.end

      // Debug: Log the date string we're receiving
      console.log(`🕐 [HomeSlideshow] Countdown ${index + 1} - end date string:`, endDateString)

      if (!endDateString || endDateString.trim() === '') {
        console.error(`🕐 [HomeSlideshow] Countdown ${index + 1}: No end date provided`)
        return
      }

      // Parse date as local time
      // Format expected from Liquid: "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DD HH:MM"
      // JavaScript Date constructor can parse "YYYY-MM-DD HH:MM:SS" but interprets it as UTC
      // We need to parse it as local time
      let parsedDate
      try {
        // Try multiple parsing strategies
        // Strategy 1: Replace hyphens with slashes to force local time interpretation
        let localDateString = endDateString.trim()

        // If format is "YYYY-MM-DD HH:MM:SS", replace first two hyphens with slashes
        // This makes it "YYYY/MM/DD HH:MM:SS" which JavaScript interprets as local time
        if (localDateString.match(/^\d{4}-\d{2}-\d{2}/)) {
          localDateString = localDateString.replace(
            /^(\d{4})-(\d{2})-(\d{2})/,
            '$1/$2/$3'
          )
        }

        parsedDate = new Date(localDateString)

        // Validate the date
        if (isNaN(parsedDate.getTime())) {
          console.error(
            `🕐 [HomeSlideshow] Countdown ${index + 1}: Invalid date format:`,
            endDateString
          )
          console.error(
            `🕐 [HomeSlideshow] Countdown ${index + 1}: Parsed string was:`,
            localDateString
          )
          return
        }
      } catch (error) {
        console.error(`🕐 [HomeSlideshow] Countdown ${index + 1}: Error parsing date:`, error)
        console.error(`🕐 [HomeSlideshow] Countdown ${index + 1}: Original string:`, endDateString)
        return
      }

      const endTime = parsedDate.getTime()

      // Debug: Log parsed date
      console.log(`🕐 [HomeSlideshow] Countdown ${index + 1} - Parsed end time:`, parsedDate)
      console.log(`🕐 [HomeSlideshow] Countdown ${index + 1} - Current time:`, new Date())
      console.log(
        `🕐 [HomeSlideshow] Countdown ${index + 1} - Time difference (ms):`,
        endTime - new Date().getTime()
      )
      console.log(
        `🕐 [HomeSlideshow] Countdown ${index + 1} - Time difference (hours):`,
        (endTime - new Date().getTime()) / (1000 * 60 * 60)
      )

      const elements = {
        days: countdown.querySelector('[data-countdown-days]'),
        daysContainer: countdown.querySelector(
          '[data-countdown-days-container]'
        ),
        hours: countdown.querySelector('[data-countdown-hours]'),
        minutes: countdown.querySelector('[data-countdown-minutes]'),
        seconds: countdown.querySelector('[data-countdown-seconds]'),
      }

      // Check if all required elements exist
      if (!elements.hours || !elements.minutes || !elements.seconds) {
        console.error(
          `🕐 [HomeSlideshow] Countdown ${index + 1}: Missing required countdown elements`
        )
        return
      }

      function updateCountdown() {
        const now = new Date().getTime()
        const distance = endTime - now

        if (distance <= 0) {
          if (elements.daysContainer) {
            elements.daysContainer.style.display = 'none'
          }
        if (elements.hours) elements.hours.textContent = '00'
        if (elements.minutes) elements.minutes.textContent = '00'
        if (elements.seconds) elements.seconds.textContent = '00'
        if (countdown._countdownInterval) {
          clearInterval(countdown._countdownInterval)
          countdown._countdownInterval = null
        }
        return
        }

        const days = Math.floor(distance / (1000 * 60 * 60 * 24))
        const hours = Math.floor((distance / (1000 * 60 * 60)) % 24)
        const minutes = Math.floor((distance / (1000 * 60)) % 60)
        const seconds = Math.floor((distance / 1000) % 60)

        if (elements.daysContainer) {
          elements.daysContainer.style.display = days > 0 ? 'flex' : 'none'
        }
        if (elements.days) {
          elements.days.textContent = String(days).padStart(2, '0')
        }
        if (elements.hours) {
          elements.hours.textContent = String(hours).padStart(2, '0')
        }
        if (elements.minutes) {
          elements.minutes.textContent = String(minutes).padStart(2, '0')
        }
        if (elements.seconds) {
          elements.seconds.textContent = String(seconds).padStart(2, '0')
        }

        // Show the countdown with smooth transition after updating values
        requestAnimationFrame(() => {
          countdown.classList.remove('opacity-0')
          countdown.classList.add('opacity-100')
        })
      }

      // Initial update
      updateCountdown()

      // Set up interval
      const interval = setInterval(updateCountdown, 1000)

      // Store interval on the element for potential cleanup
      countdown._countdownInterval = interval
    })
  }
}
